import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import { ArrowUpRight, Check, CircleHelp, MapPin, Phone, Shield, Sparkles } from "lucide-react";

function AreaChip({ area, extended = false }: { area: string; extended?: boolean }) {
  return (
    <li
      className={`group flex min-h-11 items-center gap-3 rounded-xl border px-3.5 py-2.5 text-[15px] leading-tight transition-transform duration-200 hover:-translate-y-0.5 ${
        extended
          ? "border-[#d7cce8] bg-[#fbf9fd] text-[#3c3153] hover:border-[#b6a2d2] hover:bg-[#f5f0fa]"
          : "border-[#ddd2e7] bg-[#fffdfd] text-[#30263f] hover:border-[#b59dcf] hover:bg-[#faf6fc]"
      }`}
    >
      <span
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
          extended ? "bg-[#e8def0] text-[#674d7d]" : "bg-[#ece3f1] text-[#644777]"
        }`}
        aria-hidden="true"
      >
        <Check size={13} strokeWidth={2.5} />
      </span>
      <span>{area}</span>
    </li>
  );
}

export default function ServiceAreas() {
  const primaryAreas = [
    "All Saints",
    "Arbor Hill",
    "Betton Hills",
    "Betton Oaks",
    "Bradford Woods",
    "Brandt / Mary’s",
    "Buck Lake",
    "Canopy",
    "Cascades",
    "Cherry Laurel",
    "East Tallahassee",
    "Evergreen Community",
    "Eastgate",
    "Falls Chase",
    "Indianhead Acres / Lehigh",
    "Killearn Acres",
    "Killearn Estates",
    "Los Robles",
    "Lakeshore",
    "Lafayette Oaks",
    "Levy Park",
    "Killearn Estates",
    "Lafayette Park",
    "Linene Woods",
    "Melody Hills",
    "Miccosukee Hills",
    "Myers Park",
    "Northeast Tallahassee",
    "Old Town",
    "Parkside",
    "Piedmont / Live Oak",
    "Piney Z",
    "Rhoden Cove",
    "Swift Creek",
    "Velda Dairy",
    "The Vineyards",
    "Waverly Hills",
    "Weems Plantation",
    "Westminster Oaks",
    "Woodgate",
  ];

  const extendedAreas = [
    "Baum Rd",
    "Bobbin Brook",
    "Bradfordville",
    "Buckhead",
    "Buck Lake Woods",
    "Bull Run",
    "Centerville Conservation",
    "Foxcroft",
    "Golden Eagle",
    "Kerry Forest",
    "Killearn Lakes",
    "Lake Jackson",
    "Lake Iomonia",
    "Ox Bottom Manor",
    "Piney Z",
    "Rose Lawn",
    "Rose Hill",
    "Southwood",
    "Summerbrooke",
    "Summerlake",
    "Winwood Hills",
  ];

  return (
    <div className="flex flex-col w-full">
      <SEO
        title="Service Areas | Dog Walking & Pet Sitting Coverage"
        description="Tallahassee Lassie Pet Sitter and Dog Walker Service Areas. This page lists our in area and extended area services for pet sitting and dog walking in Tallahassee, as well as pet care services such as transportation to and from groomer / vet / trainer / etc."
        path="/service-areas"
      />
      <section className="relative overflow-hidden bg-[#f8f4f2]">
        <div className="relative container mx-auto max-w-6xl px-5 py-12 sm:px-8 md:py-20">
          <div className="pointer-events-none absolute -right-32 -top-28 h-80 w-80 rounded-full bg-[#eadff0]/70 blur-3xl" aria-hidden="true" />
          <div className="pointer-events-none absolute -left-48 top-[38rem] h-96 w-96 rounded-full bg-[#efe4dc]/70 blur-3xl" aria-hidden="true" />

          <header className="relative mb-12 max-w-3xl md:mb-16">
            <div className="mb-6 flex items-center gap-3 text-sm font-bold uppercase tracking-[0.16em] text-[#6d4e7e]">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e4d8eb]">
                <MapPin size={18} strokeWidth={1.8} />
              </span>
              <span>Where we walk</span>
            </div>
            <h1 className="max-w-3xl text-4xl leading-[1.06] text-[#30263f] sm:text-5xl md:text-[4.35rem]">
              Tallahassee pet care,
              <br />
              close to home.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-8 text-[#5c5265]">
              Tallahassee Lassie provides dog walking and pet sitting across
              Tallahassee and Leon County, from familiar city neighborhoods to
              extended-area homes.
            </p>
          </header>

          <section
            aria-labelledby="coverage-answer"
            className="relative mb-8 max-w-4xl rounded-[1.5rem] border border-[#d9cbe3] bg-[#fffdfd] px-6 py-5 shadow-[0_12px_30px_rgba(72,45,91,0.05)] sm:px-8"
          >
            <p className="mb-1 text-xs font-bold uppercase tracking-[0.2em] text-[#765684]">
              Coverage at a glance
            </p>
            <h3 id="coverage-answer" className="sr-only">
              Tallahassee Lassie service area coverage
            </h3>
            <p className="text-base leading-7 text-[#4d414b]">
              <strong className="font-bold text-[#30263f]">Yes—we likely serve your Tallahassee neighborhood.</strong>{" "}
              Our standard service area includes city-limits neighborhoods below,
              and we serve extended areas when availability allows.
            </p>
          </section>

          <div className="relative grid gap-7 lg:grid-cols-[1.2fr_0.8fr] lg:items-start">
            <section
              aria-labelledby="in-area-heading"
              className="relative overflow-hidden rounded-[2rem] border border-[#d9cbe3] bg-[#f1e9f4] p-6 shadow-[0_18px_45px_rgba(72,45,91,0.08)] sm:p-9 md:p-10"
            >
              <div className="absolute -right-14 -top-14 h-44 w-44 rounded-full border-[18px] border-[#e1d3e9]/70" aria-hidden="true" />
              <div className="relative">
                <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-[#765684]">
                      The neighborhood route
                    </p>
                    <h3 id="in-area-heading" className="text-3xl text-[#30263f] sm:text-[2.15rem]">
                      In-Area Neighborhoods
                    </h3>
                  </div>
                  <span className="rounded-full bg-[#6b4c7d] px-3.5 py-2 text-xs font-bold uppercase tracking-[0.12em] text-[#fffafc]">
                    Standard area
                  </span>
                </div>
                <p className="mb-8 max-w-lg text-base leading-7 text-[#594965]">
                  Most of our VIP Program clients are in these city-limits
                  neighborhoods. This is our everyday territory, where we know
                  the best sidewalks, shady loops, and friendly faces.
                </p>
                <ul className="grid gap-2.5 sm:grid-cols-2" aria-label="In-area neighborhoods">
                  {primaryAreas.map((area, index) => (
                    <AreaChip key={`${area}-${index}`} area={area} />
                  ))}
                </ul>
              </div>
            </section>

            <section
              aria-labelledby="extended-heading"
              className="relative overflow-hidden rounded-[2rem] border border-[#d8c9e0] bg-[#fffaf6] p-6 shadow-[0_18px_45px_rgba(72,45,91,0.07)] sm:p-9 md:p-10 lg:mt-16"
            >
              <div className="absolute -right-8 top-8 rotate-12 text-[#e7d9e7]" aria-hidden="true">
                <Sparkles size={78} strokeWidth={0.8} />
              </div>
              <div className="relative">
                <div className="mb-8">
                  <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-[#8b6680]">
                    A little farther out
                  </p>
                  <div className="flex items-start justify-between gap-3">
                    <h3 id="extended-heading" className="text-3xl text-[#30263f] sm:text-[2.15rem]">
                      Extended Areas
                    </h3>
                    <ArrowUpRight className="mt-1 text-[#765684]" size={24} aria-hidden="true" />
                  </div>
                </div>
                <p className="mb-8 text-base leading-7 text-[#625466]">
                  We serve these areas depending on availability for an extended
                  area rate. Ask us about the best fit for your schedule.
                </p>
                <ul className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-1" aria-label="Extended areas">
                  {extendedAreas.map((area, index) => (
                    <AreaChip key={`${area}-${index}`} area={area} extended />
                  ))}
                </ul>
              </div>
            </section>
          </div>

          <aside className="relative mt-8 overflow-hidden rounded-[1.5rem] border border-[#decfc9] bg-[#eee2da] px-6 py-6 sm:flex sm:items-center sm:gap-6 sm:px-9">
            <div className="mb-4 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#f9f1eb] text-[#76516f] sm:mb-0">
              <MapPin size={20} strokeWidth={1.8} />
            </div>
            <p className="max-w-4xl text-[15px] leading-7 text-[#4d414b]">
              These are not comprehensive lists of service areas for
              Tallahassee/Leon County. If you do not see your area listed, please
              call to inquire. We will occasionally adjust our service area, so
              be sure to ask before booking.
            </p>
          </aside>

          <section
            aria-labelledby="coverage-faq-heading"
            className="relative mt-8 max-w-4xl rounded-[1.5rem] border border-[#d9cbe3] bg-[#f1e9f4]/65 p-6 sm:p-8"
          >
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#e4d8eb] text-[#6d4e7e]">
                <CircleHelp size={18} strokeWidth={1.8} aria-hidden="true" />
              </span>
              <h3 id="coverage-faq-heading" className="text-2xl text-[#30263f]">
                Service area questions
              </h3>
            </div>
            <div className="divide-y divide-[#d9cbe3]">
              <details className="group py-3 first:pt-0">
                <summary className="cursor-pointer list-none pr-8 text-base font-bold text-[#30263f] marker:hidden">
                  Do you serve neighborhoods not listed here?
                </summary>
                <p className="mt-2 max-w-3xl text-[15px] leading-7 text-[#594965]">
                  These lists are not comprehensive. Tallahassee Lassie serves all
                  of Leon County when availability allows, so please call to ask
                  about your address before booking.
                </p>
              </details>
              <details className="group py-3 last:pb-0">
                <summary className="cursor-pointer list-none pr-8 text-base font-bold text-[#30263f] marker:hidden">
                  What is an extended-area service?
                </summary>
                <p className="mt-2 max-w-3xl text-[15px] leading-7 text-[#594965]">
                  Extended-area visits depend on sitter availability and include
                  an extended-area rate. We can confirm the best fit for your
                  schedule when you inquire.
                </p>
              </details>
            </div>
          </section>
        </div>
      </section>
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <Reveal>
            <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
            <h2 className="font-serif text-3xl font-bold mb-4">Peace of Mind Guarantee</h2>
            <p className="text-lg text-muted-foreground mb-4">
              Tallahassee Lassie was named the Best in Florida and Best of Next Door 2020, 2021, 2022! Best of Florida 2022 - 2025 and Pet Services Association Best in 2024. We are happy to be of service to Tallahassee and surrounding areas!
            </p>
            <p className="text-lg text-muted-foreground mb-8">
              We offer a Peace of Mind Guarantee! After Our Meet & Greet, you'll be assigned a primary sitter, a secondary backup, and an emergency back-up, meaning your services are always covered! We are proud to be the only pet sitting and dog walking service in Tallahassee to make this promise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg h-14 px-8">
                <a href="https://www.timetopet.com/portal/tallylassiepets/create-account" target="_blank" rel="noopener noreferrer">
                  Get Started Now
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg h-14 px-8 border-primary text-primary">
                <Link href="/contact-us">
                  <Phone size={18} className="mr-2" /> Contact Us
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
